import { useState, useRef, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { X, Loader2, RefreshCw, Send, Sparkles } from 'lucide-react'
import { salesApi, agentsApi } from '../api'

export default function JarvisButton() {
  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState('diagnose') // 'diagnose' | 'chat'
  const [loading, setLoading] = useState(false)
  const [diagnosis, setDiagnosis] = useState(null)
  const [chatMessages, setChatMessages] = useState([])
  const [chatInput, setChatInput] = useState('')
  const [chatLoading, setChatLoading] = useState(false)
  const [kpisCache, setKpisCache] = useState({})
  const chatEndRef = useRef(null)
  const inputRef = useRef(null)
  const location = useLocation()

  const fetchKpis = async () => {
    try {
      const res = await salesApi.dashboard()
      const kpis = res.data?.kpis || {}
      setKpisCache(kpis)
      return kpis
    } catch {
      return {}
    }
  }

  const analyze = async () => {
    setLoading(true)
    try {
      const kpis = await fetchKpis()
      const { data } = await agentsApi.diagnose(kpis)
      setDiagnosis(data)
    } catch (err) {
      setDiagnosis({
        health_score: 0,
        summary: 'Erro ao conectar com Jarvis. Verifique se o backend está rodando.',
        suggestions: [
          { priority: 'ALTO', icon: '⚠️', title: 'Conexão falhou', detail: err?.response?.data?.detail || err?.message || 'Erro desconhecido' }
        ]
      })
    }
    setLoading(false)
  }

  const sendChat = async () => {
    const msg = chatInput.trim()
    if (!msg || chatLoading) return
    setChatInput('')
    const userMsg = { role: 'user', content: msg }
    setChatMessages(prev => [...prev, userMsg])
    setChatLoading(true)
    try {
      const kpis = Object.keys(kpisCache).length ? kpisCache : await fetchKpis()
      const history = chatMessages.slice(-10)
      const { data } = await agentsApi.chat(msg, kpis, history)
      const reply = data?.reply || '⚠️ Resposta vazia da IA. Tente novamente.'
      setChatMessages(prev => [...prev, { role: 'assistant', content: reply }])
    } catch (err) {
      setChatMessages(prev => [...prev, { role: 'assistant', content: '⚠️ Erro ao responder: ' + (err?.response?.data?.detail || err?.message || 'tente novamente') }])
    }
    setChatLoading(false)
  }

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [chatMessages, chatLoading])

  useEffect(() => {
    if (tab === 'chat' && open) inputRef.current?.focus()
  }, [tab, open])

  const handleOpen = () => {
    setOpen(true)
  }

  const handleTabChange = (t) => {
    setTab(t)
    if (t === 'chat' && chatMessages.length === 0) {
      setChatMessages([{ role: 'assistant', content: 'Olá! Sou o Jarvis 🤖 Tenho acesso aos dados do seu ERP. Pode perguntar sobre vendas, estoque, financeiro, CRM, equipe ou qualquer outra área do sistema.' }])
    }
  }

  const priorityColor = (p) => {
    const v = (p || '').toLowerCase()
    if (v === 'alto' || v === 'high') return 'text-red-400'
    if (v === 'médio' || v === 'medio' || v === 'medium') return 'text-yellow-400'
    return 'text-emerald-400'
  }

  const priorityLabel = (p) => {
    const v = (p || '').toLowerCase()
    if (v === 'alto' || v === 'high') return 'urgente'
    if (v === 'médio' || v === 'medio' || v === 'medium') return 'médio'
    return 'baixo'
  }

  const scoreColor = (s) =>
    s >= 70 ? 'text-emerald-400' : s >= 40 ? 'text-yellow-400' : 'text-red-400'

  // Jarvis só aparece na dashboard — return após todos os hooks (regra dos hooks)
  if (location.pathname !== '/' && location.pathname !== '/dashboard') return null

  return (
    <>
      {/* Botão flutuante Jarvis */}
      <div className="fixed bottom-6 right-6 z-40">
        {/* Anel de glow animado */}
        <span className="absolute inset-0 rounded-2xl animate-ping opacity-20" style={{ background: 'linear-gradient(135deg, #6c63ff, #a78bfa)' }} />
        <span className="absolute inset-[-4px] rounded-2xl opacity-30 blur-md" style={{ background: 'linear-gradient(135deg, #6c63ff, #06b6d4)' }} />

        <button
          onClick={handleOpen}
          title="Jarvis — IA do ERP"
          className="relative w-14 h-14 rounded-2xl flex flex-col items-center justify-center gap-0.5 transition-all duration-300 hover:scale-110 hover:shadow-2xl group"
          style={{
            background: 'linear-gradient(135deg, #6c63ff 0%, #4f46e5 50%, #06b6d4 100%)',
            boxShadow: '0 8px 32px rgba(108, 99, 255, 0.45), 0 2px 8px rgba(0,0,0,0.4)',
          }}
        >
          {/* Brilho interno */}
          <span className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.15), transparent)' }} />

          <Sparkles size={18} className="text-white drop-shadow" />
          <span className="text-[9px] font-bold text-white/90 tracking-widest uppercase leading-none">Jarvis</span>
        </button>
      </div>

      {open && (
        <div className="fixed bottom-20 right-6 w-96 bg-[#12121a] border border-[#2a2a3a] rounded-2xl shadow-2xl z-50 overflow-hidden animate-slide-up flex flex-col" style={{ maxHeight: '80vh' }}>
          {/* Header */}
          <div className="px-5 py-3 border-b border-[#2a2a3a] flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #6c63ff, #06b6d4)' }}>
                <Sparkles size={13} className="text-white" />
              </div>
              <div>
                <span className="font-display font-bold text-sm text-[#e8e8f0]">Jarvis</span>
                <span className="ml-1.5 text-[9px] text-[#6c63ff] font-medium uppercase tracking-wider">IA</span>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-[#6b6b80] hover:text-[#e8e8f0] p-1 transition-colors">
              <X size={14} />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-[#2a2a3a] flex-shrink-0">
            {[
              { id: 'diagnose', label: '📊 Diagnóstico' },
              { id: 'chat', label: '💬 Chat' },
            ].map(({ id, label }) => (
              <button
                key={id}
                onClick={() => handleTabChange(id)}
                className={`flex-1 py-2.5 text-xs font-medium transition-colors ${tab === id ? 'text-[#6c63ff] border-b-2 border-[#6c63ff]' : 'text-[#6b6b80] hover:text-[#e8e8f0]'}`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Diagnóstico */}
          {tab === 'diagnose' && (
            <div className="p-4 overflow-y-auto flex-1">
              {loading ? (
                <div className="flex flex-col items-center gap-3 py-8">
                  <Loader2 size={22} className="animate-spin text-[#6c63ff]" />
                  <p className="text-xs text-[#6b6b80]">Analisando seus dados...</p>
                </div>
              ) : !diagnosis ? (
                <div className="flex flex-col items-center gap-4 py-8 text-center">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #6c63ff22, #06b6d422)' }}>
                    <Sparkles size={24} className="text-[#6c63ff]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#e8e8f0] mb-1">Diagnóstico Completo</p>
                    <p className="text-xs text-[#6b6b80] leading-relaxed max-w-[220px]">Analiso todas as áreas do seu ERP e entrego um relatório priorizado.</p>
                  </div>
                  <button
                    onClick={analyze}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-xs font-semibold transition-all hover:scale-105"
                    style={{ background: 'linear-gradient(135deg, #6c63ff, #06b6d4)', boxShadow: '0 4px 16px rgba(108,99,255,0.35)' }}
                  >
                    <Sparkles size={13} /> Iniciar Análise
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Score */}
                  <div className="flex items-start gap-3 bg-[#1a1a26] rounded-xl p-3 border border-[#2a2a3a]">
                    <div className="flex-shrink-0 text-center">
                      <div className={`text-2xl font-bold font-display ${scoreColor(diagnosis.health_score)}`}>
                        {diagnosis.health_score}
                      </div>
                      <div className="text-[10px] text-[#6b6b80]">score</div>
                    </div>
                    <div className="flex-1">
                      <div className="h-1.5 rounded-full bg-[#2a2a3a] mb-2">
                        <div
                          className={`h-full rounded-full transition-all ${diagnosis.health_score >= 70 ? 'bg-emerald-400' : diagnosis.health_score >= 40 ? 'bg-yellow-400' : 'bg-red-400'}`}
                          style={{ width: `${diagnosis.health_score}%` }}
                        />
                      </div>
                      <p className="text-xs text-[#b0b0c0] leading-relaxed">{diagnosis.summary}</p>
                    </div>
                  </div>

                  {/* Sugestões */}
                  <div className="space-y-2">
                    {(diagnosis.suggestions || []).map((s, i) => (
                      <div key={i} className="bg-[#1a1a26] rounded-lg p-3 border border-[#2a2a3a]">
                        <div className="flex items-center gap-1.5 mb-1">
                          <span>{s.icon}</span>
                          <span className={`text-xs font-medium ${priorityColor(s.priority)}`}>{s.title}</span>
                          <span className={`ml-auto text-[10px] opacity-60 ${priorityColor(s.priority)}`}>
                            {priorityLabel(s.priority)}
                          </span>
                        </div>
                        <p className="text-xs text-[#b0b0c0] leading-relaxed">{s.detail}</p>
                      </div>
                    ))}
                  </div>

                  {/* Insights */}
                  {diagnosis.insights && (
                    <div className="bg-[#1a1a26] rounded-xl p-3 border border-[#2a2a3a] space-y-2">
                      <p className="text-[10px] font-semibold text-[#6b6b80] uppercase tracking-wider">Insights por área</p>
                      {[
                        { key: 'funnel', label: '🔀 Funil' },
                        { key: 'finance', label: '💰 Financeiro' },
                        { key: 'stock', label: '📦 Estoque' },
                        { key: 'team', label: '👥 Equipe' },
                        { key: 'marketing', label: '📣 Marketing' },
                      ].map(({ key, label }) => diagnosis.insights[key] && (
                        <div key={key}>
                          <span className="text-[10px] font-medium text-[#6c63ff]">{label}</span>
                          <p className="text-[10px] text-[#b0b0c0] leading-relaxed">{diagnosis.insights[key]}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  <button onClick={analyze} className="w-full btn-secondary text-xs flex items-center justify-center gap-2">
                    <RefreshCw size={12} /> Reanalisar
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Chat */}
          {tab === 'chat' && (
            <div className="flex flex-col flex-1 min-h-0">
              {/* Mensagens */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {chatMessages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-[85%] rounded-2xl px-3 py-2 text-xs leading-relaxed ${
                        msg.role === 'user'
                          ? 'bg-[#6c63ff] text-white rounded-br-sm'
                          : 'bg-[#1a1a26] border border-[#2a2a3a] text-[#b0b0c0] rounded-bl-sm'
                      }`}
                      style={{ whiteSpace: 'pre-wrap' }}
                    >
                      {msg.content}
                    </div>
                  </div>
                ))}
                {chatLoading && (
                  <div className="flex justify-start">
                    <div className="bg-[#1a1a26] border border-[#2a2a3a] rounded-2xl rounded-bl-sm px-3 py-2">
                      <div className="flex gap-1 items-center h-4">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#6c63ff] animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-1.5 h-1.5 rounded-full bg-[#6c63ff] animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-1.5 h-1.5 rounded-full bg-[#6c63ff] animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Sugestões rápidas (só quando vazio) */}
              {chatMessages.length <= 1 && (
                <div className="px-4 pb-2 flex flex-wrap gap-1.5">
                  {[
                    'Como estão as vendas?',
                    'Qual meu lucro esse mês?',
                    'Estoque crítico?',
                    'Como melhorar o CRM?',
                  ].map(q => (
                    <button
                      key={q}
                      onClick={() => { setChatInput(q); inputRef.current?.focus() }}
                      className="text-[10px] px-2 py-1 rounded-full bg-[#1a1a26] border border-[#2a2a3a] text-[#6b6b80] hover:text-[#e8e8f0] hover:border-[#6c63ff] transition-colors"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}

              {/* Input */}
              <div className="p-3 border-t border-[#2a2a3a] flex gap-2 flex-shrink-0">
                <input
                  ref={inputRef}
                  value={chatInput}
                  onChange={e => setChatInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendChat()}
                  placeholder="Pergunte ao Jarvis..."
                  className="flex-1 bg-[#1a1a26] border border-[#2a2a3a] rounded-xl px-3 py-2 text-xs text-[#e8e8f0] placeholder-[#6b6b80] focus:outline-none focus:border-[#6c63ff]"
                />
                <button
                  onClick={sendChat}
                  disabled={!chatInput.trim() || chatLoading}
                  className="w-8 h-8 rounded-xl bg-[#6c63ff] flex items-center justify-center text-white hover:bg-[#5a52d5] transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                >
                  <Send size={13} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  )
}
